import {
  IStateMetaData,
  IMachineContext,
  TMachineActions,
  TInputTypes,
  TMachineStates,
  TRefEventsActions,
  IYardageSelectionEvent,
  TPuntEvents,
  TPuntStates,
  TMachineEvents,
  TRefEventStates,
  TPenaltyDownResult,
} from '@/types';
import type { AnyEventObject, BaseActionObject, StateNodeConfig } from 'xstate';
import {
  MAIN_MACHINE_ID,
  PRIMARY_FLOW_INPUTS,
  SELECT_DOWN_INPUTS,
  TEventTypes,
  TPlayDesigns,
  YARDAGE_SELECTOR_BUTTONS,
} from '@/constants';
import {
  CONFIRM_BASIC_META,
  HANDLE_YARDAGE_TOGGLES_META,
  handleFumbleResult,
  handleYardageToggle,
} from '../utils';
import { PUNT_REF_WF } from './refEventFlows/punt';
import { onPuntResultSelected, onPuntTypeSelected } from './nextActions/punt';

export const PUNT_MACHINE: StateNodeConfig<IMachineContext, any, AnyEventObject, BaseActionObject> = {
  initial: TPuntStates.PUNT_TYPE,
  states: {
    [TPuntStates.PUNT_TYPE]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Punt',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onPuntTypeSelected,
        onBack({ send }) {
          send(TPuntEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TPuntEvents.BACK]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.PUNT_SELECTED]: {
          target: TPuntStates.PUNT_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.FAKE_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.ON_FUMBLE]: {
          target: TPuntStates.FUMBLE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.PENALTY_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_PUNT}.${TPuntStates.REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.ON_REF]: {
          target: TPuntStates.REF_EVT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPuntStates.PUNT_RESULT]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Punt Result',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onPuntResultSelected,
        onBack({ send }) {
          send(TPuntEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TPuntEvents.BACK]: {
          target: TPuntStates.PUNT_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.TO_CONFIRMATION]: {
          target: TPuntStates.CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.TO_YARDAGE]: {
          target: TPuntStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.ON_FUMBLE]: {
          target: TPuntStates.FUMBLE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.PENALTY_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_PUNT}.${TPuntStates.REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPuntStates.FUMBLE]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Fumble Result',
          showLogo: true,
          showBackBtn: true,
        },
        onNext({ send, generalState, playFlowState, payload, context }) {
          playFlowState.pushEvent(
            {
              eventTypeId: TEventTypes.POTENTIAL_TOV,
              value: true,
            },
            generalState.currentGame.gameId
          );

          handleFumbleResult(
            TPuntEvents.ON_FUMBLE,
            context.inputForNextComponent.backTo,
            TPuntEvents.BACK_TO_FUMBLE_RECOVERY,
            payload,
            send,
            playFlowState,
            generalState,
            context
          );
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TPuntEvents.TO_START]: {
          target: TPuntStates.PUNT_TYPE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.PUNT_SELECTED]: {
          target: TPuntStates.PUNT_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.BACK_TO_PUNT_RESULT]: {
          target: TPuntStates.PUNT_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.ON_FUMBLE]: {
          target: TPuntStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.PENALTY_SELECTED]: {
          target: TPuntStates.REF_EVT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPuntStates.SELECT_YARDAGE]: {
      meta: {
        component: TInputTypes.YARDAGE_SELECTOR,
        componentConfiguration: {
          title: 'Punt Return',
          showLogo: true,
          showBackBtn: true,
        },
        onNext(inp) {
          const { payload } = inp;

          handleYardageToggle(inp, TMachineEvents.BACK_TO_YARDAGE_SELECTION, {
            side: (payload as IYardageSelectionEvent).side,
            yardLine: (payload as IYardageSelectionEvent).yardLine,
            currToggle: 0,
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo || TPuntEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TPuntEvents.BACK]: {
          target: TPuntStates.PUNT_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_PUNT}.${TPuntStates.REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.BACK_TO_FUMBLE_RECOVERY]: {
          target: TPuntStates.FUMBLE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TPuntStates.CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
          target: TPuntStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_HANDLE_YARDAGE_TOGGLE]: {
          target: TPuntStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPuntStates.CONFIRMATION]: {
      meta: CONFIRM_BASIC_META,
      on: {
        [TPuntEvents.CONFIRMED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
        [TMachineEvents.TO_PAT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.CONFIRM_WITH_SAFTEY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
          target: TPuntStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_HANDLE_YARDAGE]: {
          target: TPuntStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.BACK_TO_PUNT_RESULT]: {
          target: TPuntStates.PUNT_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_PUNT}.${TPuntStates.REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.ON_REF_EVENT]: {
          target: TPuntStates.REF_EVT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.ON_SELECT_POSSESSION]: {
          target: TPuntStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPuntStates.REF_EVT]: PUNT_REF_WF,

    [TPuntStates.HANDLE_YARDAGE_TOGGLES]: {
      meta: HANDLE_YARDAGE_TOGGLES_META,
      on: {
        [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
          target: TPuntStates.SELECT_YARDAGE,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.UNSET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.BACK_TO_SAME]: {
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_PUNT}.${TPuntStates.REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.POST_PENALTY_YARDAGE]: {
          target: TPuntStates.SELECT_YARDAGE,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TPuntStates.CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TPuntStates.SELECT_POSSESSION]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Select Possession',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: ({ send, generalState, playFlowState, payload, context }) => {
          const ctxUpdate: Partial<IMachineContext> = {
            currentPlay: {
              ...context.currentPlay,
              resultType: context.currentPlay?.resultType || [],
              options: {
                ...context.currentPlay.options,
              },
            },
          };

          // if possession is to the kicking team, go to replay/first down screen
          if (payload.value === playFlowState.activeTeam) {
            ctxUpdate.currentPlay.options.isPossessionRetained = true;

            return send(TPuntEvents.POSSESSION_SELECTED, {
              ctxUpdate,
              nextStateInput: {
                ...SELECT_DOWN_INPUTS,
                backTo: TPuntEvents.BACK_TO_SELECT_POSSESSION,
                title: 'Select Down',
              },
            });
          }

          // Explicitly mark possession as switched so confirmation does not re-prompt.
          ctxUpdate.currentPlay.options.isPossessionRetained = false;

          if (context.currentPlay.penalty) {
            return send(TPuntEvents.TO_YARDAGE, {
              ctxUpdate,
              nextStateInput: {
                buttons: YARDAGE_SELECTOR_BUTTONS(),
                sliderLabels: {
                  left: generalState.teams[playFlowState.teams.left].abbreviation,
                  right: generalState.teams[playFlowState.teams.right].abbreviation,
                },
                backTo: TRefEventsActions.BACK_TO_PENALTY,
                title: 'Post-Penalty Yardline',
              },
            });
          }

          return send(TMachineEvents.CONFIRMED, {
            ctxUpdate,
            nextStateInput: PRIMARY_FLOW_INPUTS,
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TPuntEvents.BACK_TO_SELECT_POSSESSION]: {
          target: TPuntStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_PUNT}.${TPuntStates.REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.POSSESSION_SELECTED]: {
          target: TPuntStates.SELECT_DOWN_POST_PENALTY,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.TO_YARDAGE]: {
          target: TPuntStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.CONFIRMED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
          ],
        },
      },
    },

    [TPuntStates.SELECT_DOWN_POST_PENALTY]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Select Down',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: ({ context, payload, generalState, playFlowState, send }) => {
          const ctxUpdate: Partial<IMachineContext> = {
            currentPlay: {
              ...context.currentPlay,
              resultType: context.currentPlay?.resultType || [],
              options: {
                ...context.currentPlay.options,
              },
            },
          };

          if (payload.value === TPenaltyDownResult.REPLAY_DOWN) {
            ctxUpdate.currentPlay.options.isReplayDown = true;
            ctxUpdate.currentPlay.isFirstDown = false;
          } else if (payload.value === TPenaltyDownResult.FIRST_DOWN) {
            ctxUpdate.currentPlay.options.isReplayDown = false;
            ctxUpdate.currentPlay.isFirstDown = true;
          }

          return send(TPuntEvents.TO_YARDAGE, {
            ctxUpdate,
            nextStateInput: {
              buttons: YARDAGE_SELECTOR_BUTTONS(),
              sliderLabels: {
                left: generalState.teams[playFlowState.teams.left].abbreviation,
                right: generalState.teams[playFlowState.teams.right].abbreviation,
              },
              backTo: TRefEventsActions.BACK_TO_PENALTY,
              title: ctxUpdate.currentPlay.penalty ? 'Post-Penalty Yardline' : 'Post-Play Yardline',
            },
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_PUNT}.${TPuntStates.REF_EVT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.BACK_TO_SELECT_POSSESSION]: {
          target: TPuntStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TPuntEvents.TO_YARDAGE]: {
          target: TPuntStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
  },
};
